import { useState, FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";

type Step = 1 | 2 | 3 | 4;

interface ProductEntry {
  id: string;
  name: string;
  category: string;
  samplePrice: string;
  bulkPrice: string;
  moq: string;
  description: string;
}

const CATEGORIES = [
  "Electronics", "Machinery", "Textiles & Clothing", "Food & Beverage",
  "Jewellery & Luxury", "Furniture & Home Decor", "Chemicals & Plastics",
  "Packaging", "Auto Parts", "Beauty & Fragrance", "Construction Materials",
  "Safety Equipment", "Smart Devices & IoT", "Other",
];

const COUNTRIES = [
  "UAE", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman",
  "Jordan", "Egypt", "Turkey", "Iraq", "Lebanon", "Other",
];

const WAREHOUSE_COUNTRIES = [
  "UAE", "Saudi Arabia", "Qatar", "Kuwait", "UK", "USA",
  "Germany", "France", "China", "India", "Singapore", "Australia",
];

const emptyProduct = (): ProductEntry => ({
  id: crypto.randomUUID(),
  name: "",
  category: "",
  samplePrice: "",
  bulkPrice: "",
  moq: "",
  description: "",
});

const RegisterFactoryPage = () => {
  const [step, setStep] = useState<Step>(1);
  const [submitted, setSubmitted] = useState(false);
  const [dropshipping, setDropshipping] = useState(false);
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [products, setProducts] = useState<ProductEntry[]>([emptyProduct()]);

  const toggleWarehouse = (country: string) => {
    setSelectedWarehouses((prev) =>
      prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country]
    );
  };

  const addProduct = () => setProducts((prev) => [...prev, emptyProduct()]);
  const removeProduct = (id: string) => setProducts((prev) => prev.filter((p) => p.id !== id));
  const updateProduct = (id: string, field: keyof ProductEntry, value: string) => {
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    data.set("dropshipping", dropshipping ? "Yes" : "No");
    data.set("warehouseCountries", selectedWarehouses.join(", "));
    data.set("products", JSON.stringify(products));

    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));

    try {
      await fetch("https://readdy.ai/api/form/d7ekell37cef9t402hhg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-amber-50/40">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="bg-white rounded-3xl border border-amber-100 p-12 max-w-lg w-full text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-6">
              <i className="ri-checkbox-circle-fill text-4xl text-amber-500"></i>
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Factory Registered!
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              Your factory application has been submitted. Our verification team will review your details and contact you within 2–3 business days.
            </p>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 text-left">
              <p className="text-xs font-bold text-amber-700 mb-2">What happens next?</p>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-amber-500 mt-0.5"></i>Our team verifies your factory documents</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-amber-500 mt-0.5"></i>Products are reviewed and listed on the platform</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-amber-500 mt-0.5"></i>You receive your Verified Factory badge</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-amber-500 mt-0.5"></i>Global buyers start placing sample &amp; bulk orders</li>
              </ul>
            </div>
            <a href="/" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-home-line"></i> Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { num: 1, label: "Factory Info" },
    { num: 2, label: "Products & Pricing" },
    { num: 3, label: "Dropshipping" },
    { num: 4, label: "Review & Submit" },
  ];

  return (
    <div className="min-h-screen bg-amber-50/30">
      <SeoHead
        title="Register Your Factory – List Products & Sell Globally | SuperMiddleEast"
        description="Register your Middle East factory on SuperMiddleEast. List products, set sample and bulk prices, enable dropshipping and reach millions of global buyers. First 3 months free, then $5/month."
        keywords="register factory Middle East, list factory products, sell factory products online, Dubai factory registration, Saudi Arabia supplier registration"
        canonical={`${BASE_URL}/register-factory`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Register Your Factory – SuperMiddleEast",
            "description": "Register your Middle East factory, list products with sample and bulk pricing, enable dropshipping and reach global buyers.",
            "url": `${BASE_URL}/register-factory`,
            "inLanguage": "en",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
                { "@type": "ListItem", "position": 2, "name": "Register Factory", "item": `${BASE_URL}/register-factory` },
              ],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Factory Registration – SuperMiddleEast",
            "description": "List your factory on SuperMiddleEast to reach global buyers, dropshippers and influencers. First 3 months free.",
            "url": `${BASE_URL}/register-factory`,
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "First 3 months free, then $5/month",
            },
          },
        ]}
      />
      <Navbar />
      <div className="pt-24 pb-20 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">

          {/* Free Trial Hero Banner */}
          <div className="relative rounded-3xl overflow-hidden mb-10" style={{ background: "linear-gradient(135deg, #b8860b 0%, #d4a017 30%, #f5c842 60%, #d4a017 80%, #b8860b 100%)" }}>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="absolute w-1 h-1 bg-white rounded-full opacity-50"
                  style={{ top: `${15 + (i * 11) % 70}%`, left: `${8 + (i * 17) % 85}%` }}
                ></div>
              ))}
            </div>
            <div className="relative z-10 px-8 py-10 text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 border border-white/30 rounded-full px-4 py-1.5 mb-4">
                <i className="ri-gift-line text-white text-sm"></i>
                <span className="text-white text-xs font-bold uppercase tracking-widest">Limited Time Offer</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Register Your Factory
              </h1>
              <p className="text-white/90 text-base max-w-lg mx-auto mb-6">
                List your products and start receiving orders from global buyers, stars &amp; influencers worldwide.
              </p>
              {/* Pricing highlight */}
              <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white/95 rounded-2xl px-8 py-5">
                <div className="text-center">
                  <div className="text-4xl font-black text-amber-600" style={{ fontFamily: "'Playfair Display', serif" }}>$0</div>
                  <div className="text-gray-500 text-xs font-semibold">First 3 Months</div>
                </div>
                <div className="hidden sm:flex flex-col items-center gap-1">
                  <i className="ri-arrow-right-line text-amber-400 text-xl"></i>
                  <span className="text-gray-400 text-xs">then</span>
                </div>
                <div className="sm:hidden text-gray-400 text-xs">then</div>
                <div className="text-center">
                  <div className="text-4xl font-black text-gray-800" style={{ fontFamily: "'Playfair Display', serif" }}>$5<span className="text-lg font-semibold text-gray-400">/mo</span></div>
                  <div className="text-gray-500 text-xs font-semibold">After Free Trial</div>
                </div>
                <div className="hidden sm:block w-px h-10 bg-gray-200"></div>
                <div className="flex flex-col gap-1 text-left">
                  <span className="flex items-center gap-1.5 text-xs text-gray-600">
                    <i className="ri-check-line text-amber-500"></i> No credit card required
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-gray-600">
                    <i className="ri-check-line text-amber-500"></i> Cancel anytime
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-gray-600">
                    <i className="ri-check-line text-amber-500"></i> No hidden fees
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Step label */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-1.5 rounded-full">
              <i className="ri-store-2-line"></i> Complete Your Registration Below
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-0 mb-10">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full font-bold text-sm transition-all ${
                    step > s.num ? "bg-emerald-500 text-white" :
                    step === s.num ? "bg-amber-500 text-white" :
                    "bg-gray-100 text-gray-400"
                  }`}>
                    {step > s.num ? <i className="ri-check-line"></i> : s.num}
                  </div>
                  <span className={`text-xs mt-1 font-medium whitespace-nowrap ${step === s.num ? "text-amber-600" : "text-gray-400"}`}>{s.label}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-16 md:w-24 h-0.5 mb-4 mx-1 transition-all ${step > s.num ? "bg-emerald-400" : "bg-gray-200"}`}></div>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">

            {/* Step 1: Factory Info */}
            {step === 1 && (
              <div className="bg-white rounded-2xl border border-amber-100 p-6 md:p-8 space-y-5">
                <h2 className="font-black text-gray-900 text-xl mb-1">Factory Information</h2>
                <p className="text-gray-400 text-sm mb-4">Tell us about your factory so buyers can find and trust you.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Factory Name <span className="text-red-400">*</span></label>
                    <input name="factoryName" required placeholder="e.g. Al Futtaim Industrial Group" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Country <span className="text-red-400">*</span></label>
                    <select name="country" required className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                      <option value="">Select country</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">City <span className="text-red-400">*</span></label>
                    <input name="city" required placeholder="e.g. Dubai" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Year Established <span className="text-red-400">*</span></label>
                    <input name="established" required type="number" min="1900" max="2025" placeholder="e.g. 2010" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Number of Employees</label>
                    <select name="employees" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                      <option value="">Select range</option>
                      <option>1–10</option>
                      <option>10–50</option>
                      <option>50–100</option>
                      <option>100–200</option>
                      <option>200–500</option>
                      <option>500–1000</option>
                      <option>1000+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Main Category <span className="text-red-400">*</span></label>
                    <select name="mainCategory" required className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                      <option value="">Select category</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Factory Description <span className="text-red-400">*</span></label>
                  <textarea name="description" required rows={3} maxLength={500} placeholder="Describe your factory, specializations, certifications, and what makes you unique..." className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 resize-none" />
                  <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Contact Name <span className="text-red-400">*</span></label>
                    <input name="contactName" required placeholder="Your full name" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address <span className="text-red-400">*</span></label>
                    <input name="email" required type="email" placeholder="factory@example.com" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone / WhatsApp <span className="text-red-400">*</span></label>
                    <input name="phone" required placeholder="+971 50 000 0000" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Website (optional)</label>
                    <input name="website" placeholder="https://yourfactory.com" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button type="button" onClick={() => setStep(2)} className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    Next: Products &amp; Pricing <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Products */}
            {step === 2 && (
              <div className="bg-white rounded-2xl border border-amber-100 p-6 md:p-8 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-black text-gray-900 text-xl mb-1">Products &amp; Pricing</h2>
                    <p className="text-gray-400 text-sm">Add the products you want to sell. Set sample and bulk prices.</p>
                  </div>
                  <button type="button" onClick={addProduct} className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 font-semibold text-sm px-4 py-2 rounded-xl hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line"></i> Add Product
                  </button>
                </div>

                <div className="space-y-5">
                  {products.map((product, idx) => (
                    <div key={product.id} className="border border-amber-100 rounded-2xl p-5 bg-amber-50/30">
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-bold text-gray-800 text-sm flex items-center gap-2">
                          <span className="w-6 h-6 flex items-center justify-center bg-amber-500 text-white rounded-full text-xs font-bold">{idx + 1}</span>
                          Product {idx + 1}
                        </span>
                        {products.length > 1 && (
                          <button type="button" onClick={() => removeProduct(product.id)} className="text-red-400 hover:text-red-600 text-sm cursor-pointer flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-delete-bin-line"></i> Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Product Name <span className="text-red-400">*</span></label>
                          <input value={product.name} onChange={(e) => updateProduct(product.id, "name", e.target.value)} required placeholder="e.g. Industrial LED Smart Panel 200W" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Category <span className="text-red-400">*</span></label>
                          <select value={product.category} onChange={(e) => updateProduct(product.id, "category", e.target.value)} required className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400 bg-white">
                            <option value="">Select category</option>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Min. Order Qty (MOQ) <span className="text-red-400">*</span></label>
                          <input value={product.moq} onChange={(e) => updateProduct(product.id, "moq", e.target.value)} required type="number" min="1" placeholder="e.g. 50" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Sample Price (USD/unit) <span className="text-red-400">*</span></label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                            <input value={product.samplePrice} onChange={(e) => updateProduct(product.id, "samplePrice", e.target.value)} required type="number" min="0" step="0.01" placeholder="0.00" className="w-full border border-gray-200 rounded-xl pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-amber-400" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Bulk Price (USD/unit) <span className="text-red-400">*</span></label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                            <input value={product.bulkPrice} onChange={(e) => updateProduct(product.id, "bulkPrice", e.target.value)} required type="number" min="0" step="0.01" placeholder="0.00" className="w-full border border-gray-200 rounded-xl pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-amber-400" />
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Product Description</label>
                          <textarea value={product.description} onChange={(e) => updateProduct(product.id, "description", e.target.value)} rows={2} maxLength={500} placeholder="Brief product description, specs, materials..." className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400 resize-none" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between pt-2">
                  <button type="button" onClick={() => setStep(1)} className="border border-amber-200 text-amber-700 font-bold px-6 py-3 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-arrow-left-line"></i> Back
                  </button>
                  <button type="button" onClick={() => setStep(3)} className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    Next: Dropshipping <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Dropshipping */}
            {step === 3 && (
              <div className="bg-white rounded-2xl border border-amber-100 p-6 md:p-8 space-y-6">
                <h2 className="font-black text-gray-900 text-xl mb-1">Dropshipping Settings</h2>
                <p className="text-gray-400 text-sm">Enable dropshipping to let resellers sell your products without holding inventory.</p>

                <div className="flex items-start gap-4 p-5 border-2 border-amber-200 rounded-2xl bg-amber-50/40 cursor-pointer" onClick={() => setDropshipping(!dropshipping)}>
                  <div className={`w-6 h-6 flex items-center justify-center rounded-md border-2 flex-shrink-0 mt-0.5 transition-all ${dropshipping ? "bg-amber-500 border-amber-500" : "border-gray-300"}`}>
                    {dropshipping && <i className="ri-check-line text-white text-sm"></i>}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">Enable Dropshipping for My Factory</p>
                    <p className="text-gray-500 text-xs mt-1">Resellers and influencers can list your products and earn commissions. You handle fulfillment and shipping directly to end customers.</p>
                  </div>
                </div>

                {dropshipping && (
                  <div className="space-y-5 animate-fade-in">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">Warehouse / Shipping Countries</label>
                      <p className="text-xs text-gray-400 mb-3">Select all countries you can ship from or have warehouses in:</p>
                      <div className="flex flex-wrap gap-2">
                        {WAREHOUSE_COUNTRIES.map((country) => (
                          <button
                            key={country}
                            type="button"
                            onClick={() => toggleWarehouse(country)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                              selectedWarehouses.includes(country)
                                ? "bg-emerald-500 border-emerald-500 text-white"
                                : "border-gray-200 text-gray-600 hover:border-emerald-300"
                            }`}
                          >
                            {selectedWarehouses.includes(country) && <i className="ri-check-line mr-1"></i>}
                            {country}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Dropshipping Commission (%)</label>
                        <input name="commission" type="number" min="0" max="50" placeholder="e.g. 20" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                        <p className="text-xs text-gray-400 mt-1">Commission paid to resellers per sale</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Average Shipping Time</label>
                        <select name="shippingTime" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                          <option value="">Select timeframe</option>
                          <option>1–3 business days</option>
                          <option>3–7 business days</option>
                          <option>7–14 business days</option>
                          <option>14–21 business days</option>
                          <option>21–30 business days</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Private Label / Custom Branding</label>
                      <div className="flex gap-3">
                        {["Yes, available", "No", "On request"].map((opt) => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name="privateLabelOption" value={opt} className="accent-amber-500" />
                            <span className="text-sm text-gray-700">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Additional Dropshipping Notes</label>
                      <textarea name="dropshippingNotes" rows={2} maxLength={500} placeholder="Any special terms, restrictions, or notes for dropshippers..." className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 resize-none" />
                    </div>
                  </div>
                )}

                {!dropshipping && (
                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center">
                    <i className="ri-truck-line text-2xl text-gray-300 mb-2 block"></i>
                    <p className="text-gray-400 text-sm">Dropshipping is disabled. You can enable it anytime after registration.</p>
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <button type="button" onClick={() => setStep(2)} className="border border-amber-200 text-amber-700 font-bold px-6 py-3 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-arrow-left-line"></i> Back
                  </button>
                  <button type="button" onClick={() => setStep(4)} className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    Review &amp; Submit <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="bg-white rounded-2xl border border-amber-100 p-6 md:p-8 space-y-6">
                <h2 className="font-black text-gray-900 text-xl mb-1">Review &amp; Submit</h2>
                <p className="text-gray-400 text-sm">Almost done! Review your information and submit your factory registration.</p>

                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-building-2-line text-amber-600"></i>
                      <span className="font-bold text-gray-800 text-sm">Factory Details</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <span><strong>Products added:</strong> {products.length}</span>
                      <span><strong>Dropshipping:</strong> {dropshipping ? "Enabled" : "Disabled"}</span>
                      {dropshipping && selectedWarehouses.length > 0 && (
                        <span className="col-span-2"><strong>Warehouses:</strong> {selectedWarehouses.join(", ")}</span>
                      )}
                    </div>
                  </div>

                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-shield-check-line text-emerald-600 text-lg mt-0.5"></i>
                      <div>
                        <p className="font-bold text-gray-800 text-sm mb-1">Verification Process</p>
                        <p className="text-gray-500 text-xs">After submission, our team will verify your factory within 2–3 business days. You&apos;ll receive a Verified Factory badge once approved.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-amber-400 rounded-full flex-shrink-0">
                        <i className="ri-gift-line text-white text-lg"></i>
                      </div>
                      <div>
                        <p className="font-bold text-amber-800 text-sm">Your first 3 months are completely FREE!</p>
                        <p className="text-amber-700 text-xs mt-0.5">After the free trial, membership is only <strong>$5/month</strong>. No credit card needed now. Cancel anytime.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <input type="checkbox" id="terms" name="terms" required className="mt-1 accent-amber-500" />
                    <label htmlFor="terms" className="text-xs text-gray-600 cursor-pointer">
                      I confirm that all information provided is accurate and I agree to the{" "}
                      <span className="text-amber-600 font-semibold">SuperMiddleEast Terms of Service</span> and{" "}
                      <span className="text-amber-600 font-semibold">Factory Seller Agreement</span>.
                    </label>
                  </div>
                </div>

                <div className="flex justify-between pt-2">
                  <button type="button" onClick={() => setStep(3)} className="border border-amber-200 text-amber-700 font-bold px-6 py-3 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-arrow-left-line"></i> Back
                  </button>
                  <button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-store-2-line"></i> Submit Factory Registration
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RegisterFactoryPage;
